import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Theme, useMediaQuery } from "@mui/material";
import { Investment } from "@rentablo/api/apiv1";
import NewsIcon from "@icons/myicons/news.svg?react";
import ListIcon from "@icons/myicons/list.svg?react";
import ChartIcon from "@icons/myicons/chart.svg?react";
import DollarIcon from "@icons/myicons/dollar.svg?react";
import { Tabs, Tab } from "@rentablo/components";
import { RangeSelectorControlProps } from "@hooks";
import { useMandatorVisibility } from "@helpers";
import {
  FundamentalsPageByInvestmentId,
  FundamentalsProvider,
  FundamentalsActions,
} from "../../../fundamentals";
import { InvestmentNewsFeedList } from "./InvestmentNewsFeedList";
import { InvestmentProtocolContainer } from "./InvestmentProtocolContainer";
import { DividendsTableContainer } from "./DividendsTableContainer";

export const tabKeys = {
  fundamentals: "fundamentals",
  news: "news",
  protocol: "protocol",
  dividends: "dividends",
};

interface Props {
  investmentId: number;
  investment?: Investment;
  isInvestmentLoading: boolean;
  quoteValue: number;
  displayCurrency?: string;
  isOriginal: boolean;
  protocolRangeProps: RangeSelectorControlProps;
}

export const InvestmentPageTabs = ({
  investmentId,
  investment,
  isInvestmentLoading,
  quoteValue,
  displayCurrency,
  isOriginal,
  protocolRangeProps,
}: Props) => {
  const { t } = useTranslation();
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const isNewsTabVisible = useMandatorVisibility({ hideFor: ["kpmg-germany"] });

  const defaultTabKey = searchParams.get("tab");

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  return (
    <FundamentalsProvider investment={investment}>
      <Tabs
        onChange={handleTabChange}
        defaultKey={defaultTabKey}
        buttonElement={
          <FundamentalsActions fundamentalsTabKey={tabKeys.fundamentals} />
        }
      >
        <Tab
          label={isMd ? <ChartIcon /> : t("investment.tabs.fundamentals")}
          key={tabKeys.fundamentals}
        >
          <FundamentalsActions
            activeTab={tabKeys.fundamentals}
            fundamentalsTabKey={tabKeys.fundamentals}
            inTabsBar={false}
            fullWidth
          />
          <FundamentalsPageByInvestmentId investmentId={investmentId} />
        </Tab>
        {investment?.isin && isNewsTabVisible && (
          <Tab
            label={isMd ? <NewsIcon /> : t("investment.tabs.news")}
            key={tabKeys.news}
          >
            <InvestmentNewsFeedList isin={investment.isin} />
          </Tab>
        )}
        {!isInvestmentLoading &&
          investment &&
          investment.quote?.last != null && (
            <Tab
              label={isMd ? <ListIcon /> : t("investment.tabs.protocol")}
              key={tabKeys.protocol}
            >
              <InvestmentProtocolContainer
                investmentId={investmentId}
                investmentQuote={quoteValue}
                currencySymbol={displayCurrency}
                currentMarketValue={
                  isOriginal
                    ? (investment.currentMarketValue ?? 0)
                    : (investment.currentMarketValueAC ?? 0)
                }
                quoteNotation={investment.quoteNotation}
                rangeProps={protocolRangeProps}
              />
            </Tab>
          )}
        <Tab
          label={isMd ? <DollarIcon /> : t("investment.tabs.dividends")}
          key={tabKeys.dividends}
        >
          <DividendsTableContainer />
        </Tab>
      </Tabs>
    </FundamentalsProvider>
  );
};
